import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { grnApi, grnErr, qtyText, when } from '@/services/grnApi';

/** Deliveries with goods counted on POs that are not on a final GRN yet — tap one to preview and finalise. */
export const GrnToFinalise = ({ onOpen, refreshKey }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(() => {
    grnApi.toFinalise({ q }).then((r) => setRows(r.deliveries)).catch((e) => { setRows([]); toast.error(grnErr(e, 'Could not load deliveries')); });
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);
  return (
    <div className="space-y-3" data-testid="grn-to-finalise">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search delivery, supplier, invoice or PO no" className="h-10 pl-9" data-testid="grn-tf-search" />
      </div>
      {rows === null ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-gray-400" />
        : !rows.length ? <p className="py-10 text-center text-sm text-gray-500" data-testid="grn-tf-empty">Every delivery counted on a PO already has a final GRN.</p> : (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((d) => (
              <button key={d.entry_id} type="button" onClick={() => onOpen(d.entry_id)} data-testid={`grn-tf-${d.entry_id}`}
                className="rounded-lg border border-[#E5DFD0] bg-white p-3 text-left transition-colors duration-150 hover:border-[#2D5F4F] hover:bg-emerald-50/40">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm font-semibold text-[#1A1F2E]">{d.document_barcode || d.entry_id}</span>
                  {d.reopened ? <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">{d.reopened} reopened</Badge>
                    : <span className="text-xs text-gray-500">{d.section}</span>}
                </div>
                <p className="truncate text-sm text-gray-800">{d.vendor_name}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                  {d.vendor_doc_no && <span>Invoice {d.vendor_doc_no}</span>}
                  {d.po_numbers.map((n) => <Badge key={n} variant="outline" className="border-blue-300 font-mono text-[10px] text-blue-700">{n}</Badge>)}
                </div>
                <p className="mt-1 text-xs"><span className="font-semibold text-emerald-700" data-testid={`grn-tf-qty-${d.entry_id}`}>{qtyText(d.counted)} counted</span>
                  <span className="text-gray-500"> · {d.receipt_count} receipt(s) · last {when(d.last_counted_at)}</span></p>
              </button>))}
          </div>)}
    </div>
  );
};

export default GrnToFinalise;
